import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Upload, Loader2, Layers, ArrowRight, MapPin, Calendar, Clock } from "lucide-react";
import { downscaleImage } from "@/lib/eventLib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicMasterForm({ token }) {
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState("count"); // count | fill | done
  const [count, setCount] = useState(1);
  const [entries, setEntries] = useState([]);
  const [photoCache, setPhotoCache] = useState({});
  const [uploading, setUploading] = useState({});
  const [busy, setBusy] = useState(false);
  const [sentCount, setSentCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/master/${token}`)
      .then((r) => setEv(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Master link not found"));
  }, [token]);

  const startFill = () => {
    const n = Math.max(1, Math.min(500, Number(count) || 1));
    setEntries(Array.from({ length: n }, () => ({})));
    setStep("fill");
    window.scrollTo(0, 0);
  };

  const setField = (idx, k, v) =>
    setEntries((list) => list.map((e, i) => (i === idx ? { ...e, [k]: v } : e)));

  const uploadPhoto = async (idx, key, file) => {
    if (!file) return;
    const uk = `${idx}-${key}`;
    setUploading((u) => ({ ...u, [uk]: true }));
    try {
      const durl = await downscaleImage(file, 800, 0.8);
      const r = await axios.post(`${API}/public/master/${token}/photos`, { data: durl });
      setField(idx, key, r.data.id);
      setPhotoCache((c) => ({ ...c, [r.data.id]: durl }));
      toast.success("Photo upload ho gayi");
    } catch { toast.error("Photo upload failed, dobara try karein"); }
    setUploading((u) => ({ ...u, [uk]: false }));
  };

  const submitAll = async () => {
    if (Object.values(uploading).some(Boolean)) return toast.error("Photo upload ho rahi hai, thoda ruko...");
    for (let i = 0; i < entries.length; i++) {
      for (const f of ev.fields || []) {
        if (f.required && (!entries[i][f.key] || (Array.isArray(entries[i][f.key]) && entries[i][f.key].length === 0))) return toast.error(`Entry ${i + 1}: "${f.label}" zaroori hai`);
      }
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/master/${token}/submit`, { entries: entries.map((e) => Object.fromEntries(Object.entries(e).map(([k, v]) => [k, Array.isArray(v) ? v.join(", ") : v]))) });
      setSentCount(r.data.count || entries.length);
      setStep("done");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  const renderField = (idx, f) => {
    const val = entries[idx][f.key] || "";
    if (f.type === "number") return <Input type="number" value={val} onChange={(e) => setField(idx, f.key, e.target.value)} data-testid={`m-field-${idx}-${f.key}`} />;
    if (f.type === "date") return <Input type="date" value={val} onChange={(e) => setField(idx, f.key, e.target.value)} data-testid={`m-field-${idx}-${f.key}`} />;
    if (f.type === "time") return <Input type="time" value={val} onChange={(e) => setField(idx, f.key, e.target.value)} data-testid={`m-field-${idx}-${f.key}`} />;
    if (f.type === "longtext") return <textarea rows={2} value={val} onChange={(e) => setField(idx, f.key, e.target.value)} className="w-full text-sm border border-input rounded-md px-3 py-2 resize-y" data-testid={`m-field-${idx}-${f.key}`} />;
    if (f.type === "dropdown") return (
      <Select value={val} onValueChange={(v) => setField(idx, f.key, v)}>
        <SelectTrigger data-testid={`m-field-${idx}-${f.key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>{(f.options || []).filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
    if (f.type === "checkbox") {
      const arr = Array.isArray(val) ? val : [];
      return (
        <div className="space-y-1.5" data-testid={`m-field-${idx}-${f.key}`}>
          {(f.options || []).filter(Boolean).map((o) => {
            const on = arr.includes(o);
            return (
              <label key={o} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`m-field-${idx}-${f.key}-opt-${o.replace(/\s+/g, "-")}`}>
                <input type="checkbox" checked={on} onChange={() => setField(idx, f.key, on ? arr.filter((x) => x !== o) : [...arr, o])} className="h-4 w-4 accent-pink-600" />
                {o}
              </label>
            );
          })}
        </div>
      );
    }
    if (f.type === "photo") return (
      <div className="flex items-center gap-2">
        <label className={`inline-flex items-center gap-1.5 text-sm border rounded-md px-3 py-2 ${uploading[`${idx}-${f.key}`] ? "pointer-events-none opacity-80 bg-cyan-50 text-cyan-700 border-cyan-200" : "cursor-pointer hover:bg-slate-50"}`} data-testid={`m-field-${idx}-${f.key}`}>
          {uploading[`${idx}-${f.key}`] ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4" /> {val ? "Change" : "Upload"}</>}
          <input type="file" accept="image/*" hidden disabled={!!uploading[`${idx}-${f.key}`]} onChange={(e) => { uploadPhoto(idx, f.key, e.target.files[0]); e.target.value = ""; }} />
        </label>
        {val && photoCache[val] && <img src={photoCache[val]} alt="" className="h-12 w-12 rounded object-cover border" />}
      </div>
    );
    return <Input value={val} onChange={(e) => setField(idx, f.key, e.target.value)} data-testid={`m-field-${idx}-${f.key}`} />;
  };

  if (err) return <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50"><div className="text-center"><img src="/logo.webp?v=2" alt="" className="h-10 mx-auto mb-3" /><p className="text-red-500 font-semibold" data-testid="master-error">{err}</p></div></div>;
  if (!ev) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-600" /></div>;

  if (step === "done") {
    return (
      <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="master-done">
        <div className="max-w-lg mx-auto bg-white border border-border rounded-2xl shadow-sm p-8 text-center space-y-3">
          <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
          <h1 className="font-heading text-xl font-bold" data-testid="master-done-title">{sentCount} entries bhej di gayi!</h1>
          <p className="text-sm text-muted-foreground">Aapki {sentCount} entries "{ev.name}" ke liye approval ke liye receive ho gayi hain.</p>
          <Button variant="outline" onClick={() => { setStep("count"); setEntries([]); setCount(1); }} data-testid="master-again-btn">Aur entries add karein</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="public-master-form">
      <div className="max-w-2xl mx-auto space-y-4">
        {ev.banner && <img src={ev.banner} alt={ev.name} className="w-full aspect-[1200/630] object-cover rounded-2xl border shadow-sm" />}
        <div className="bg-white border border-border rounded-2xl shadow-sm p-6 space-y-2">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-bold text-pink-700 bg-pink-50 px-2 py-0.5 rounded-full w-fit"><Layers className="h-3.5 w-3.5" /> Bulk Entry (Master Link)</p>
          <h1 className="font-heading text-xl font-bold" data-testid="master-title">{ev.name}</h1>
          {ev.description && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{ev.description}</p>}
          {(ev.city || ev.event_date || ev.event_time) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
              {ev.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-pink-600" /> {ev.city}</span>}
              {ev.event_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-pink-600" /> {ev.event_date}</span>}
              {ev.event_time && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-pink-600" /> {ev.event_time}</span>}
            </div>
          )}
        </div>

        {step === "count" && (
          <div className="bg-white border border-border rounded-2xl shadow-sm p-6 space-y-4" data-testid="master-count-step">
            <p className="font-heading font-semibold">Kitni entries banani hain?</p>
            <p className="text-xs text-muted-foreground">Ek number daalein (max 500). Uske baad utne forms khul jayenge.</p>
            <Input type="number" min={1} max={500} value={count} onChange={(e) => setCount(e.target.value)} className="h-12 text-lg font-bold text-center w-40" data-testid="master-count-input" />
            <Button onClick={startFill} className="w-full h-11 bg-pink-600 hover:bg-pink-700 text-white font-semibold gap-2" data-testid="master-start-btn">
              {Math.max(1, Math.min(500, Number(count) || 1))} forms kholein <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {step === "fill" && (<>
          {entries.map((_, idx) => (
            <div key={idx} className="bg-white border border-border rounded-2xl shadow-sm p-5 space-y-3" data-testid={`master-entry-${idx}`}>
              <p className="font-heading font-bold text-sm text-pink-700">Entry #{idx + 1}</p>
              {(ev.fields || []).map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label>{f.label}{f.required ? " *" : ""}</Label>
                  {renderField(idx, f)}
                </div>
              ))}
            </div>
          ))}
          <div className="sticky bottom-3 flex gap-2">
            <Button variant="outline" onClick={() => setStep("count")} className="h-11 bg-white" data-testid="master-back-btn">Back</Button>
            <Button onClick={submitAll} disabled={busy || Object.values(uploading).some(Boolean)} className="flex-1 h-11 bg-pink-600 hover:bg-pink-700 text-white font-semibold" data-testid="master-submit-btn">
              {busy ? "Submitting..." : `Submit ${entries.length} entries for Approval`}
            </Button>
          </div>
        </>)}
      </div>
    </div>
  );
}
